import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Table, Card, Typography, Button, Row, Col, Statistic, DatePicker, message } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, DollarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency, toApiNumber } from '../../../utils/formatters';
import { readCachedSystemConfig } from '../../../shared/lib/systemConfig';
import dayjs from 'dayjs';

const { Title } = Typography;

const CustomerPaymentHistory = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [statsPayments, setStatsPayments] = useState([]);
  const [statsTotalPaid, setStatsTotalPaid] = useState(0);
  const [totalOwed, setTotalOwed] = useState(0);
  const cfg = readCachedSystemConfig();
  const mainCode = cfg?.currency_code || 'IQD';
  const secondaryCode = cfg?.secondary_currency_code || '';

  // Helper to parse total_owed string to number
  const parseOwed = (owed) => {
    return toApiNumber(owed, 0);
  };

  const fetchCustomerPayments = useCallback(async () => {
    try {
      const response = await apiClient.get(`/customers/${id}/payments`);
      setPayments(response.data.payments || []);
      setCustomerInfo({
        id: response.data.customer_id,
        name: response.data.customer_name,
        total_owed: response.data.total_owed // Directly use total_owed from API
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payments:', error);
      message.error(t('fetchError'));
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchCustomerPayments();
  }, [fetchCustomerPayments]);

  // Defensive: always treat dateRange as array, fallback to [null, null] if null
  const safeDateRange = useMemo(
    () => (Array.isArray(dateRange) ? dateRange : [null, null]),
    [dateRange]
  );

  const filteredPayments = payments.filter(payment => {
    if (!safeDateRange[0] || !safeDateRange[1]) return true;
    const paymentDate = dayjs(payment.created_at);
    return safeDateRange[0] && safeDateRange[1] &&
      paymentDate.isAfter(safeDateRange[0].startOf('day').subtract(1, 'ms')) &&
      paymentDate.isBefore(safeDateRange[1].endOf('day').add(1, 'ms'));
  });

  // Refactored to prevent infinite re-renders
  useEffect(() => {
    setStatsPayments((safeDateRange[0] && safeDateRange[1]) ? filteredPayments : payments);
  }, [safeDateRange, filteredPayments, payments]);

  useEffect(() => {
    setStatsTotalPaid(statsPayments.reduce((sum, p) => sum + toApiNumber(p.amount, 0), 0));
  }, [statsPayments]);

  useEffect(() => {
    setTotalOwed(parseOwed(customerInfo?.total_owed));
  }, [customerInfo]);

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: <b>{t('رقم الدفعة')}</b>,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: <b>{t('تاريخ الدفعة')}</b>,
      dataIndex: 'created_at',
      key: 'created_at',
      render: function(text) {
        if (!text) return '';
        const date = new Date(text);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
      },
    },
    {
      title: <b>{t('المبلغ')}</b>,
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) => {
        const amount = toApiNumber(text, 0);
        const isRefund = record.payment_method === 'cancel_refund' || amount < 0;
        return (
          <span style={{ fontWeight: 700, color: isRefund ? '#cf1322' : undefined }}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      title: <b>{t('تفاصيل الدفع')}</b>,
      key: 'currency_breakdown',
      render: (_, record) => {
        const main = toApiNumber(record.amount_main, Number.NaN);
        const sec = toApiNumber(record.amount_secondary, 0);
        if (!Number.isFinite(main) && sec <= 0) return '-';

        const parts = [];
        if (Number.isFinite(main)) {
          parts.push(`${main.toLocaleString()} ${mainCode}`);
        }
        if (sec > 0) {
          parts.push(`${sec.toLocaleString()} ${secondaryCode || 'secondary'}`);
        }
        const rate = record?.exchange_rate?.main_per_secondary;
        const rateText = rate
          ? ` | 1 ${mainCode} = ${Number(rate).toLocaleString()} ${secondaryCode || ''}`.trim()
          : '';
        return `${parts.join(' + ')}${rateText}`;
      },
    },
    {
      title: <b>{t('طريقة الدفع')}</b>,
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (text) => {
        if (text === 'cancel_refund') {
          return <Tag color="red">{t('cancelRefund', 'Cancel refund')}</Tag>;
        }
        return text || '-';
      },
    },
    {
      title: <b>{t('المستلم')}</b>,
      dataIndex: 'receiver',
      key: 'receiver',
      render: (text) => text || '-',
    },
    {
      title: <b>{t('المسلم')}</b>,
      dataIndex: 'sender',
      key: 'sender',
      render: (text) => text || '-',
    },
    {
      title: <b>{t('الإيصال')}</b>,
      key: 'actions',
      render: (_, record) => (
        <Link to={`/GetCustomer/PaymentRecipt/${id}/${record.id}`} target="_blank">
          <Button icon={<PrinterOutlined />} style={{ background: '#faad14', color: '#fff', border: 'none' }} />
        </Link>
      ),
    },
  ];

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([null, null]); // Clear the date range without causing errors
    }
  };

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Invalid or Missing Customer ID</h1>
      </div>
    );
  }
  const datePickerValue = safeDateRange.every(date => date === null) ? undefined : safeDateRange;

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {customerInfo ? `${customerInfo.name}` : t('سجل الدفعات')}
          </Title>
          <Link to={`/GetCustomer`}>
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              {t('back')}
            </Button>
          </Link>
        </div>
      </Card>
      <Row gutter={[24, 24]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('عدد الدفعات')}
              value={statsPayments.length}
              prefix={<DollarOutlined />}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('إجمالي المدفوع')}
              value={formatCurrency(statsTotalPaid)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card style={{ background: '#fffbe6', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('إجمالي الدين')} 
              value={formatCurrency(totalOwed)} 
              valueStyle={{ color: totalOwed > 0 ? '#cf1322' : '#3f8600' }}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ direction: 'ltr', width: 'fit-content' }}>
          <DatePicker.RangePicker
            value={datePickerValue}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            style={{ minWidth: 260 }}
            allowClear
            placeholder={[t('start Date'), t('end Date')]}
            inputReadOnly
          />
        </div>
      </div>
      <Card
        style={{
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Table
          dataSource={filteredPayments}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default CustomerPaymentHistory;
